import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { getCommonCodeColumnOptions } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultEQM2010_R01HeaderDto, EQM2010_R01HeaderDto } from './Dto_EQM2010_R01';
import { useEQM2010_R01Store } from './Store_EQM2010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'EQM2010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM2010_R01HeaderDto> | null>;
	handleCreateClick: () => void;
}

export const Table_EQM2010_R01 = (props: Props) => {
	const { gridRef, handleCreateClick } = props;

	const {
		EQM2010_R01List,
		selectedEQM2010_R01,
		setSelectedEQM2010_R01,
		initSelectedEQM2010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useEQM2010_R01Store();

	const { eqmTypeList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'equipmentCd',
		gridData: EQM2010_R01List,
		defaultGridData: defaultEQM2010_R01HeaderDto,
		selectedRow: selectedEQM2010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		equipmentCd: '설비코드',
		equipmentNm: '설비명',
		equipmentType: '설비유형',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<EQM2010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 119,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '설비코드', colId: 'equipmentCd', field: 'equipmentCd', width: 107,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentCd'),
		},
		{
			headerName: '설비명', colId: 'equipmentNm', field: 'equipmentNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentNm'),
		},
		{
			headerName: '설비유형', colId: 'equipmentType', field: 'equipmentType', width: 118,
			...getCommonCodeColumnOptions(eqmTypeList, false, undefined, undefined),
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentType'),
		},
	], [getCellStyle, eqmTypeList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<EQM2010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<EQM2010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<EQM2010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedEQM2010_R01?.equipmentCd === rowData.equipmentCd;
		if (isSelected) {
			setSelectedEQM2010_R01(undefined);
			handleCreateClick();
		} else {
			setSelectedEQM2010_R01(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedEQM2010_R01, setSelectedEQM2010_R01, handleCreateClick, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: `${EQM2010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [EQM2010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [EQM2010_R01List, selectedEQM2010_R01]);

	return (
		<GridContextWrapper<EQM2010_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedEQM2010_R01}
			defaultRowData={defaultEQM2010_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<EQM2010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<EQM2010_R01HeaderDto>({
					idKey: 'equipmentCd',
					selectedList: selectedEQM2010_R01 ? [selectedEQM2010_R01.equipmentCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
